import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { css } from "@/styled-system/css";
import { forwardRef, useCallback } from "react";
import Source from "../Source/Source.esm.js";
import Video from "../Video/Video.esm.js";
const baseBackgroundVideoClass = css({
  pointerEvents: "none",
  position: "absolute",
  top: "0",
  right: "0",
  bottom: "0",
  left: "0",
  width: "100%",
  height: "100%"
});
const BackgroundVideo = forwardRef(({
  size,
  position,
  className,
  style,
  ...props
}, ref) => {
  const objectFit = size && typeof size === "object" ? size.default : size;
  const objectPosition = position && typeof position === "object" ? position.default : position;
  return /* @__PURE__ */ jsx(Video, { ref, className: `${baseBackgroundVideoClass}${className ? ` ${className}` : ""}`, style: {
    objectFit,
    objectPosition,
    ...style
  }, ...props });
});
BackgroundVideo.displayName = "BackgroundVideo";
const MobileViewportVideo = forwardRef((props, ref) => {
  const {
    hideOnMobile,
    className,
    ...rest
  } = props;
  const displayStyles = {
    display: hideOnMobile ? "none" : "block",
    lg: {
      display: "none"
    }
  };
  return /* @__PURE__ */ jsx(BackgroundVideo, { ...rest, ref, className: `${css(displayStyles)} exclude-from-algorithmic-darkening${className ? ` ${className}` : ""}` });
});
MobileViewportVideo.displayName = "MobileViewportVideo";
const TabletViewportVideo = forwardRef((props, ref) => {
  const {
    hideOnMobile,
    hideOnTablet,
    className,
    ...rest
  } = props;
  const displayStyles = {
    display: hideOnMobile ? "none" : "block",
    lg: {
      display: hideOnTablet ? "none" : "block"
    },
    xl: {
      display: "none"
    }
  };
  return /* @__PURE__ */ jsx(BackgroundVideo, { ...rest, ref, className: `${css(displayStyles)} exclude-from-algorithmic-darkening${className ? ` ${className}` : ""}` });
});
TabletViewportVideo.displayName = "TabletViewportVideo";
const DesktopViewportVideo = forwardRef((props, ref) => {
  const {
    hideOnMobile,
    hideOnTablet,
    hideOnDesktop,
    className,
    ...rest
  } = props;
  const displayStyles = {
    display: hideOnMobile ? "none" : "block",
    lg: {
      display: hideOnTablet ? "none" : "block"
    },
    xl: {
      display: hideOnDesktop ? "none" : "block"
    }
  };
  return /* @__PURE__ */ jsx(BackgroundVideo, { ...rest, ref, className: `${css(displayStyles)} exclude-from-algorithmic-darkening${className ? ` ${className}` : ""}` });
});
DesktopViewportVideo.displayName = "DesktopViewportVideo";
const GroupBackgroundVideo = ({
  position = {
    default: "50% 50%"
  },
  size = {
    default: "cover"
  },
  source,
  visible = {
    default: true,
    lg_lte: true,
    sm_lte: true
  }
}) => {
  var _a, _b, _c;
  const muteVideoOnMountRef = useMuteVideoOnMountRef();
  const sourceMap = source;
  const desktopSource = ((_a = sourceMap == null ? void 0 : sourceMap.default) == null ? void 0 : _a.src) ? sourceMap.default : void 0;
  const tabletSource = ((_b = sourceMap == null ? void 0 : sourceMap.lg_lte) == null ? void 0 : _b.src) ? sourceMap.lg_lte : void 0;
  const mobileSource = ((_c = sourceMap == null ? void 0 : sourceMap.sm_lte) == null ? void 0 : _c.src) ? sourceMap.sm_lte : void 0;
  const hasBackgroundVideo = desktopSource || tabletSource || mobileSource;
  if (!hasBackgroundVideo) {
    return null;
  }
  const visibleMap = visible;
  const commonVideoProps = {
    autoPlay: true,
    muted: true,
    loop: true,
    playsInline: true,
    size,
    position
  };
  const hasMobileVideo = !!mobileSource;
  const hasTabletVideo = !!tabletSource;
  const isVisibleOnDesktop = !!(visibleMap == null ? void 0 : visibleMap.default);
  const isVisibleOnTablet = !!(visibleMap == null ? void 0 : visibleMap.lg_lte);
  const isVisibleOnMobile = !!(visibleMap == null ? void 0 : visibleMap.sm_lte);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    hasMobileVideo && /* @__PURE__ */ jsx(MobileViewportVideo, { ...commonVideoProps, hideOnMobile: !isVisibleOnMobile, className: "mobile-video", ref: muteVideoOnMountRef, children: /* @__PURE__ */ jsx(Source, { src: mobileSource == null ? void 0 : mobileSource.src }) }),
    hasTabletVideo && /* @__PURE__ */ jsx(TabletViewportVideo, { hideOnMobile: !isVisibleOnMobile || hasMobileVideo, hideOnTablet: !isVisibleOnTablet, ...commonVideoProps, className: "tablet-video", ref: muteVideoOnMountRef, children: /* @__PURE__ */ jsx(Source, { src: tabletSource == null ? void 0 : tabletSource.src }) }),
    desktopSource && /* @__PURE__ */ jsx(DesktopViewportVideo, { ...commonVideoProps, className: "desktop-video", hideOnMobile: !isVisibleOnMobile || hasMobileVideo || hasTabletVideo, hideOnTablet: !isVisibleOnTablet || hasTabletVideo, hideOnDesktop: !isVisibleOnDesktop, ref: muteVideoOnMountRef, children: /* @__PURE__ */ jsx(Source, { src: desktopSource.src }) })
  ] });
};
function useMuteVideoOnMountRef() {
  return useCallback(function muteVideoOnMount(ref) {
    if (!ref) return;
    ref.setAttribute("muted", "");
  }, []);
}
export {
  GroupBackgroundVideo as default
};
